import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AtSign, Check, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useConsumerAuth } from "@/contexts/ConsumerAuthContext";
import { toast } from "sonner";

/**
 * UsernamePrompt — fires once, the first time a signed-in user is missing
 * a `username`. Explicitly frames it as a **nickname** — never asks for a
 * real name and clearly explains why (privacy on a medical-marijuana site).
 *
 * Also mountable as a controlled dialog from account settings for later
 * changes.
 */
const RANDO_ADJECTIVES = [
  "silver", "cosmic", "mellow", "verdant", "hazy", "amber", "rustic",
  "canyon", "misty", "quiet", "sunset", "midnight",
];
const RANDO_NOUNS = [
  "leaf", "cloud", "tortoise", "grove", "meadow", "shore", "canyon",
  "orbit", "harbor", "willow", "otter", "falcon",
];

function suggest() {
  const a = RANDO_ADJECTIVES[Math.floor(Math.random() * RANDO_ADJECTIVES.length)];
  const n = RANDO_NOUNS[Math.floor(Math.random() * RANDO_NOUNS.length)];
  const num = Math.floor(10 + Math.random() * 89);
  return `${a}-${n}-${num}`;
}

export const UsernamePrompt = ({ forceOpen, onClose }) => {
  const { user, refreshUser } = useConsumerAuth();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedThisSession, setSavedThisSession] = useState(false);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      setValue(user?.username || suggest());
      return;
    }
    // If the user already has a nickname (from server) OR we just saved one
    // in this session, never auto-open — no matter how many times the auth
    // context re-emits.
    if (!user || user.username || savedThisSession) {
      if (open && !forceOpen) setOpen(false);
      return;
    }
    // Signed in, no nickname yet, first time — auto-fire once.
    setValue(suggest());
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, forceOpen, savedThisSession]);

  const close = () => {
    setOpen(false);
    onClose?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = value.trim();
    if (!/^[A-Za-z0-9][A-Za-z0-9_\-]{2,29}$/.test(v)) {
      toast.error("3–30 chars · letters/numbers/-/_ · must start with letter or number");
      return;
    }
    setBusy(true);
    try {
      await api.post("/consumer/username", { username: v });
      toast.success("Nickname saved");
      // Close immediately + latch — do NOT wait for refreshUser to complete,
      // so there's no window where the effect could re-open the dialog.
      setSavedThisSession(true);
      setOpen(false);
      onClose?.();
      refreshUser?.().catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(o) : close())}>
      <DialogContent data-testid="username-prompt" className="max-w-md">
        <DialogTitle className="font-serif text-2xl flex items-center gap-2">
          <AtSign className="w-5 h-5 text-[#d4a268]" /> Pick a nickname
        </DialogTitle>
        <DialogDescription className="text-sm text-foreground/70 leading-relaxed">
          Bud Link never shows your real name in reviews or comments — you
          pick a public nickname instead. This is intentional privacy on a
          medical-marijuana site.
        </DialogDescription>

        <form onSubmit={submit} className="mt-4 space-y-3">
          <div className="flex gap-2">
            <div className="flex items-center px-3 rounded-md border border-border bg-secondary text-foreground/60 text-sm">
              @
            </div>
            <Input
              data-testid="username-input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              maxLength={30}
              autoFocus
              placeholder="your-handle"
              className="h-11 flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => setValue(suggest())}
              className="rounded-full h-11 px-3"
              title="Suggest another"
              data-testid="username-suggest"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            3–30 characters, letters/numbers/-/_ only. Must start with a letter
            or number. You can change it once every 30 days.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            {!!user.username && (
              <Button
                type="button"
                variant="ghost"
                onClick={close}
                className="rounded-full h-10"
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="username-save"
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6"
            >
              {busy ? "Saving…" : (<><Check className="w-4 h-4 mr-1.5" /> Save nickname</>)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UsernamePrompt;
